/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    useAuthContext,
    useAuthRoute,
    useLogoutAndRedirect,
} from '@mpb-com/auth-sdk/client';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, {
    FunctionComponent,
    ReactNode,
    useCallback,
    useContext,
} from 'react';
import styled from 'styled-components';

import { NavigationLinks } from '@toucan/common/services/navigation/types';
import { Breadcrumb as BreadcrumbType } from '@toucan/common/services/taxonomy/config/types';

import { DataLayerEventType } from '@toucan/client/global/analytics';
import {
    ApplicationContext,
    MediaQuery,
    ModalSize,
    useMediaQuery,
} from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';

import { ConfirmModal } from '@toucan/client/shared/components/confirm-modal';
import { ConstrainedWrapper } from '@toucan/client/shared/components/constrained-wrapper';
import { Link, LinkSize } from '@toucan/client/shared/components/link';
import { trackEventsViaDataLayer } from '@toucan/client/shared/helpers/analytics';
import { resetIntercomSession } from '@toucan/client/shared/helpers/intercom';

import { Breadcrumb } from '@toucan/client/blocks/components/breadcrumb';

import {
    Footer,
    FooterVariant,
    Header,
    PreFooter,
    StickyFooter,
} from '@toucan/client/features/layout';
import { getAccountNavigation } from '@toucan/client/features/layout/components/account-layout/helpers/navigation';
import { getDefaultFooterLinks } from '@toucan/client/features/layout/helpers/footer-links';

import { styles } from './account-layout.styles';
import {
    SidebarNavLink,
    SidebarNavigation,
} from './components/sidebar-navigation';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface AccountLayoutProps {
    /**
     * Root pathname of the account section
     *
     * @default '/account'
     */
    rootPathname?: string;

    /**
     * Title of the page
     */
    title?: string;

    /**
     * Child elements
     */
    children?: ReactNode;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
    isSellBalanceEnabled?: boolean;
    navigationLinks: NavigationLinks;
}

const StyledAccountLayout = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const AccountLayout: FunctionComponent<AccountLayoutProps> = ({
    title,
    children,
    rootPathname = '/account',
    'data-testid': testId = 'account-layout',
    isSellBalanceEnabled = false,
    navigationLinks,
    ...otherProps
}: AccountLayoutProps) => {
    const { pathname, push } = useRouter();
    const { t } = useTranslation();
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);

    const { setModalContent, setModalVisible, legalLinks } =
        useContext(ApplicationContext);
    const { activeMarket } = useContext(MarketContext);
    const { user, isLoggedIn } = useAuthContext();

    const isRootPath = pathname === rootPathname;
    const showSidebarNav = isSmallTablet && !isRootPath ? false : true;
    const showContent = isSmallTablet && isRootPath ? false : true;
    const showTitle = !(title && isSmallTablet && isRootPath);
    const showUserFirstName = isSmallTablet && !isRootPath ? false : true;

    const accountNavigation = getAccountNavigation(
        t,
        activeMarket.slug,
        isSellBalanceEnabled
    );
    const loginSlug = t('login', { ns: 'urls' });
    const accountSlug = t('account', { ns: 'urls' });
    const helpSlug = t('help', { ns: 'urls' });
    const contactSlug = t('contact-us', { ns: 'urls' });

    const loginPath = `/${activeMarket.slug}/${loginSlug}`;

    // useAuthRoute re-runs its redirect effect whenever `navigate`'s identity
    // changes, so this must stay stable across renders that aren't about
    // auth state, or an unrelated re-render while logged out re-triggers the
    // redirect.
    const navigate = useCallback(
        (path: string) => push(`/${loginSlug}`, path),
        [push, loginSlug]
    );

    const { showContent: showRoute } = useAuthRoute({
        navigate,
        loginPath,
    });

    const logoutAndRedirect = useLogoutAndRedirect({
        redirectPath: loginPath,
        redirect: (path) => {
            window.location.pathname = path;
        },
        onLoggedOut: () => {
            resetIntercomSession();
            trackEventsViaDataLayer([
                {
                    event: DataLayerEventType.USER_LOADED,
                    userId: null,
                    loginStatus: false,
                },
                {
                    event: DataLayerEventType.LOGOUT,
                    userId: user?.customerId,
                },
            ]);
        },
    });

    const links = getDefaultFooterLinks(t, activeMarket, legalLinks);

    const contentTestId = `${testId}__content`;

    /**
     * Finds the active link from the navigation array
     */
    function findActiveLinkInNavigation(
        links: SidebarNavLink[],
        pathname: string
    ): SidebarNavLink | false {
        let result: SidebarNavLink | false = false;

        links.forEach((parentLink) => {
            if (result !== false) {
                return;
            }

            if (parentLink.href === pathname) {
                result = parentLink;
                return;
            }

            parentLink.children?.forEach((link) => {
                if (result !== false) {
                    return;
                }

                if (link.href === pathname) {
                    result = link;
                }
            });
        });

        return result;
    }

    function getBreadcrumbLinks(
        isRootPath: boolean,
        navLinks: SidebarNavLink[],
        pathname: string
    ) {
        const accountTemplatePath = isRootPath ? undefined : `/${accountSlug}`;
        const accountHref = isRootPath
            ? undefined
            : `/${activeMarket.slug}/${accountSlug}`;

        const sharedProps = {
            size: LinkSize.SMALL,
        };

        const links: BreadcrumbType[] = [
            {
                children: t('Account'),
                href: accountHref,
                templatePath: accountTemplatePath,
                ...sharedProps,
            },
        ];

        if (!isRootPath) {
            const activeLink = findActiveLinkInNavigation(navLinks, pathname);

            if (activeLink) {
                links.push({
                    children: activeLink.content,
                    ...sharedProps,
                });
            }
        }

        return links;
    }

    function handleLogoutClick() {
        const handleModalCancel = () => {
            setModalVisible(false);
        };

        const handleModalConfirm = () => {
            logoutAndRedirect(`/${activeMarket.slug}`);
        };

        setModalContent(
            <ConfirmModal
                message={
                    <p>
                        {t('Are you sure you want to log out of your account?')}
                    </p>
                }
                onConfirm={handleModalConfirm}
                onCancel={handleModalCancel}
            />
        );

        setModalVisible(true, { size: ModalSize.SMALL });
    }

    const breadcrumbLinks = getBreadcrumbLinks(
        isRootPath,
        accountNavigation,
        pathname
    );

    return (
        <StyledAccountLayout
            rootPathname={rootPathname}
            data-testid={testId}
            isSellBalanceEnabled
            navigationLinks={navigationLinks}
            {...otherProps}
        >
            <Header
                data-testid="site-header"
                loggedIn={isLoggedIn}
                navigationLinks={navigationLinks}
            />
            <ConstrainedWrapper className="site-breadcrumbs">
                <Breadcrumb links={breadcrumbLinks} />
            </ConstrainedWrapper>
            {showRoute && (
                <ConstrainedWrapper>
                    {showUserFirstName && (
                        <Heading
                            size={HeadingSize.H1}
                            className="site-content-first-name"
                            data-testid="account-layout__greeting"
                        >
                            {t('Hi, {{firstName}}', {
                                firstName: user?.firstName,
                            })}
                        </Heading>
                    )}
                    <main>
                        {showSidebarNav && (
                            <aside className="site-sidebar-nav">
                                <SidebarNavigation
                                    navigation={accountNavigation}
                                />

                                <div className="site-sidebar-nav-buttons">
                                    <Link
                                        onClick={handleLogoutClick}
                                        className="site-sidebar-nav-logout"
                                        data-testid="account-layout__logout"
                                    >
                                        {t('Logout')}
                                    </Link>
                                    <WrappedLink
                                        href="/help/contact"
                                        as={`/${activeMarket.slug}/${helpSlug}/${contactSlug}`}
                                        className="site-sidebar-nav-faq"
                                        data-testid="account-layout__faq"
                                    >
                                        {t('Go to help and FAQs')}
                                    </WrappedLink>
                                </div>
                            </aside>
                        )}
                        {showContent && (
                            <div
                                className="site-content"
                                data-testid={contentTestId}
                            >
                                {showTitle && (
                                    <Heading
                                        size={HeadingSize.H2}
                                        className="site-content-title"
                                        data-testid="account-layout__title"
                                    >
                                        {title}
                                    </Heading>
                                )}
                                {children}
                            </div>
                        )}
                    </main>
                </ConstrainedWrapper>
            )}
            <PreFooter />
            <StickyFooter />
            {!isSmallTablet && (
                <Footer
                    linkLists={links}
                    variant={FooterVariant.DESKTOP}
                    data-testid="site-footer"
                />
            )}
        </StyledAccountLayout>
    );
};
